import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { AlertTriangle, RefreshCw, Save } from 'lucide-react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { navDb, auth } from '../../../utils/firebase/client';
import { toast } from 'sonner';

type VoicePolicyMode = 'DEFAULT_ONLY' | 'FLEET_CHOICE' | 'DISPATCHER_MANDATORY';

interface DispatcherVoiceOption {
  id: string;
  name: string;
  embeddingPath: string;
}

interface VoicePolicyAuditEvent {
  id: string;
  actorUid: string;
  actorEmail: string;
  actorRole: string;
  reason: string;
  source: string;
  createdAt: any;
  oldPolicyEnabled?: boolean;
  newPolicyEnabled?: boolean;
  oldPolicy?: Record<string, unknown>;
  newPolicy?: Record<string, unknown>;
}

interface FleetVoicePolicyPanelProps {
  companyId: string;
  companyName: string;
  initialPolicyEnabled?: boolean;
}

function formatTimestamp(ts: any): string {
  if (!ts) return '—';
  try {
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function FleetVoicePolicyPanel({
  companyId,
  companyName,
  initialPolicyEnabled = false,
}: FleetVoicePolicyPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [voicePolicyEnabled, setVoicePolicyEnabled] = useState(initialPolicyEnabled);
  const [mode, setMode] = useState<VoicePolicyMode>('DEFAULT_ONLY');
  const [dispatcherVoiceId, setDispatcherVoiceId] = useState('');
  const [dispatcherVoiceName, setDispatcherVoiceName] = useState('');
  const [dispatcherEmbeddingPath, setDispatcherEmbeddingPath] = useState('');
  const [allowMemberVoicePurchases, setAllowMemberVoicePurchases] = useState(false);

  const [dispatcherVoices, setDispatcherVoices] = useState<DispatcherVoiceOption[]>([]);
  const [auditEvents, setAuditEvents] = useState<VoicePolicyAuditEvent[]>([]);
  const [policySnapshot, setPolicySnapshot] = useState<Record<string, unknown> | null>(null);
  const [policyEnabledSnapshot, setPolicyEnabledSnapshot] = useState(Boolean(initialPolicyEnabled));

  const companyRef = useMemo(() => doc(navDb, 'nav_companies', companyId), [companyId]);
  const policyRef = useMemo(() => doc(navDb, 'nav_companies', companyId, 'voice_policy', 'default'), [companyId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [companySnap, policySnap, dispatcherSnap, auditSnap] = await Promise.all([
        getDoc(companyRef),
        getDoc(policyRef),
        getDocs(collection(navDb, 'nav_companies', companyId, 'dispatcher_voices')),
        getDocs(
          query(
            collection(navDb, 'nav_companies', companyId, 'voice_policy_audit'),
            orderBy('createdAt', 'desc'),
            limit(20),
          ),
        ),
      ]);

      if (companySnap.exists()) {
        const data = companySnap.data();
        setVoicePolicyEnabled(Boolean(data.voicePolicyEnabled));
        setPolicyEnabledSnapshot(Boolean(data.voicePolicyEnabled));
      }

      if (policySnap.exists()) {
        const data = policySnap.data();
        setPolicySnapshot(data);
        setMode((typeof data.mode === 'string' ? data.mode : 'DEFAULT_ONLY') as VoicePolicyMode);
        setDispatcherVoiceId(typeof data.dispatcherVoiceId === 'string' ? data.dispatcherVoiceId : '');
        setDispatcherVoiceName(typeof data.dispatcherVoiceName === 'string' ? data.dispatcherVoiceName : '');
        setDispatcherEmbeddingPath(typeof data.dispatcherVoiceEmbeddingPath === 'string' ? data.dispatcherVoiceEmbeddingPath : '');
        setAllowMemberVoicePurchases(Boolean(data.allowMemberVoicePurchases));
      } else {
        setPolicySnapshot(null);
        setMode('DEFAULT_ONLY');
        setDispatcherVoiceId('');
        setDispatcherVoiceName('');
        setDispatcherEmbeddingPath('');
        setAllowMemberVoicePurchases(false);
      }

      const voices = dispatcherSnap.docs
        .map((d) => {
          const v = d.data();
          const embeddingPath =
            typeof v.voiceEmbeddingPath === 'string'
              ? v.voiceEmbeddingPath
              : typeof v.dispatcherVoiceEmbeddingPath === 'string'
                ? v.dispatcherVoiceEmbeddingPath
                : '';
          const active = typeof v.isActive === 'boolean' ? v.isActive : true;
          return {
            id: d.id,
            name: typeof v.name === 'string' && v.name.trim() ? v.name.trim() : 'Dispatcher Voice',
            embeddingPath,
            active,
          };
        })
        .filter((v) => v.active && v.embeddingPath.trim().length > 0)
        .map((v) => ({ id: v.id, name: v.name, embeddingPath: v.embeddingPath }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setDispatcherVoices(voices);

      setAuditEvents(
        auditSnap.docs.map((d) => {
          const a = d.data();
          return {
            id: d.id,
            actorUid: typeof a.actorUid === 'string' ? a.actorUid : 'unknown',
            actorEmail: typeof a.actorEmail === 'string' ? a.actorEmail : 'unknown',
            actorRole: typeof a.actorRole === 'string' ? a.actorRole : 'unknown',
            reason: typeof a.reason === 'string' ? a.reason : 'unknown',
            source: typeof a.source === 'string' ? a.source : 'unknown',
            createdAt: a.createdAt ?? null,
            oldPolicyEnabled: typeof a.oldPolicyEnabled === 'boolean' ? a.oldPolicyEnabled : undefined,
            newPolicyEnabled: typeof a.newPolicyEnabled === 'boolean' ? a.newPolicyEnabled : undefined,
            oldPolicy: a.oldPolicy,
            newPolicy: a.newPolicy,
          } as VoicePolicyAuditEvent;
        }),
      );
    } catch (error) {
      console.error('Failed to load fleet voice policy panel data:', error);
      toast.error('Failed to load voice policy data');
    } finally {
      setLoading(false);
    }
  }, [companyId, companyRef, policyRef]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!dispatcherVoiceId) return;
    const selected = dispatcherVoices.find((v) => v.id === dispatcherVoiceId);
    if (!selected) return;
    setDispatcherVoiceName(selected.name);
    setDispatcherEmbeddingPath(selected.embeddingPath);
  }, [dispatcherVoiceId, dispatcherVoices]);

  const handleSave = useCallback(async () => {
    if (
      voicePolicyEnabled &&
      mode === 'DISPATCHER_MANDATORY' &&
      (!dispatcherVoiceId.trim() || !dispatcherEmbeddingPath.trim())
    ) {
      toast.error('Dispatcher mandatory mode requires a valid dispatcher voice');
      return;
    }

    setSaving(true);
    try {
      const actorUid = auth.currentUser?.uid || 'unknown';
      const actorEmail = auth.currentUser?.email || 'unknown';

      await updateDoc(companyRef, {
        voicePolicyEnabled,
        updatedAt: serverTimestamp(),
      });

      const normalizedPolicy = {
        mode,
        dispatcherVoiceId: dispatcherVoiceId.trim() || null,
        dispatcherVoiceName: dispatcherVoiceName.trim() || null,
        dispatcherVoiceEmbeddingPath: dispatcherEmbeddingPath.trim() || null,
        allowMemberVoicePurchases,
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
      };

      await setDoc(policyRef, normalizedPolicy, { merge: true });

      await addDoc(collection(navDb, 'nav_companies', companyId, 'voice_policy_audit'), {
        actorUid,
        actorEmail,
        actorRole: 'super_admin',
        oldPolicy: policySnapshot || {},
        newPolicy: normalizedPolicy,
        oldPolicyEnabled: policyEnabledSnapshot,
        newPolicyEnabled: voicePolicyEnabled,
        reason: 'super_admin_settings_update',
        source: 'oilwellx_super_admin_fleet_manager',
        createdAt: serverTimestamp(),
      });

      setPolicySnapshot({
        mode,
        dispatcherVoiceId: dispatcherVoiceId.trim() || null,
        dispatcherVoiceName: dispatcherVoiceName.trim() || null,
        dispatcherVoiceEmbeddingPath: dispatcherEmbeddingPath.trim() || null,
        allowMemberVoicePurchases,
        updatedBy: actorUid,
      });

      setPolicyEnabledSnapshot(voicePolicyEnabled);
      toast.success('Voice policy saved');
      await loadAll();
    } catch (error) {
      console.error('Failed to save voice policy:', error);
      toast.error('Failed to save voice policy');
    } finally {
      setSaving(false);
    }
  }, [
    companyId,
    companyRef,
    dispatcherEmbeddingPath,
    dispatcherVoiceId,
    dispatcherVoiceName,
    initialPolicyEnabled,
    loadAll,
    mode,
    policyRef,
    policySnapshot,
    allowMemberVoicePurchases,
    voicePolicyEnabled,
  ]);

  const handleEmergencyReset = useCallback(async () => {
    const confirmReset = window.confirm(
      `Reset ${companyName} to DEFAULT_ONLY and disable policy enforcement? This is intended for emergency rollback.`,
    );
    if (!confirmReset) return;

    setSaving(true);
    try {
      const actorUid = auth.currentUser?.uid || 'unknown';
      const actorEmail = auth.currentUser?.email || 'unknown';

      await updateDoc(companyRef, {
        voicePolicyEnabled: false,
        updatedAt: serverTimestamp(),
      });

      const resetPolicy = {
        mode: 'DEFAULT_ONLY',
        dispatcherVoiceId: null,
        dispatcherVoiceName: null,
        dispatcherVoiceEmbeddingPath: null,
        allowMemberVoicePurchases: false,
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
      };

      await setDoc(policyRef, resetPolicy, { merge: true });

      await addDoc(collection(navDb, 'nav_companies', companyId, 'voice_policy_audit'), {
        actorUid,
        actorEmail,
        actorRole: 'super_admin',
        oldPolicy: policySnapshot || {},
        newPolicy: resetPolicy,
        oldPolicyEnabled: policyEnabledSnapshot,
        newPolicyEnabled: false,
        reason: 'super_admin_emergency_reset',
        source: 'oilwellx_super_admin_fleet_manager',
        createdAt: serverTimestamp(),
      });

      setPolicyEnabledSnapshot(false);
      toast.success('Emergency reset applied');
      await loadAll();
    } catch (error) {
      console.error('Failed to run emergency reset:', error);
      toast.error('Emergency reset failed');
    } finally {
      setSaving(false);
    }
  }, [companyId, companyName, companyRef, loadAll, policyRef, policySnapshot, voicePolicyEnabled]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Navigation Voice Policy</CardTitle>
          <CardDescription className="text-xs">Loading policy data...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Navigation Voice Policy</CardTitle>
        <CardDescription className="text-xs">
          Super admin override for fleet voice behavior and purchase lock.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-amber-50 px-3 py-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={voicePolicyEnabled}
              onChange={(e) => setVoicePolicyEnabled(e.target.checked)}
            />
            Enable fleet voice policy enforcement
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Rollout switch. If disabled, app falls back to non-enforced behavior.
          </p>
        </div>

        <div>
          <Label>Voice Mode</Label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as VoicePolicyMode)}
            disabled={!voicePolicyEnabled}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="DEFAULT_ONLY">Default only</option>
            <option value="FLEET_CHOICE">Fleet choice (Default or Dispatcher)</option>
            <option value="DISPATCHER_MANDATORY">Dispatcher mandatory</option>
          </select>
        </div>

        <div>
          <Label>Dispatcher Voice</Label>
          {dispatcherVoices.length === 0 ? (
            <p className="text-xs text-gray-500 mt-1">
              No dispatcher voice configured under nav_companies/{companyId}/dispatcher_voices.
            </p>
          ) : (
            <select
              value={dispatcherVoiceId}
              onChange={(e) => setDispatcherVoiceId(e.target.value)}
              disabled={!voicePolicyEnabled}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Select dispatcher voice</option>
              {dispatcherVoices.map((voice) => (
                <option key={voice.id} value={voice.id}>
                  {voice.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <Label>Dispatcher Embedding Path</Label>
          <Input
            value={dispatcherEmbeddingPath}
            onChange={(e) => setDispatcherEmbeddingPath(e.target.value)}
            placeholder="nav_companies/{companyId}/dispatcher_voices/{voiceId}/embedding"
            disabled={!voicePolicyEnabled}
          />
        </div>

        <div className="rounded-md border px-3 py-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allowMemberVoicePurchases}
              onChange={(e) => setAllowMemberVoicePurchases(e.target.checked)}
              disabled={!voicePolicyEnabled}
            />
            Allow fleet members to purchase/select paid voices
          </label>
        </div>

        {voicePolicyEnabled && mode === 'DISPATCHER_MANDATORY' && (!dispatcherVoiceId || !dispatcherEmbeddingPath) && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Mandatory mode requires a dispatcher voice and embedding path.
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Policy
          </Button>
          <Button variant="outline" onClick={handleEmergencyReset} disabled={saving}>
            Emergency Reset
          </Button>
          <Badge variant="outline">Company: {companyName}</Badge>
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs font-medium text-gray-600 mb-2">Policy Audit Trail</p>
          {auditEvents.length === 0 ? (
            <p className="text-xs text-gray-500">No policy audit events yet.</p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {auditEvents.map((event) => {
                const newMode = typeof event.newPolicy?.mode === 'string' ? event.newPolicy.mode : '—';
                return (
                  <div key={event.id} className="rounded border px-2 py-1.5 text-xs bg-gray-50">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{event.reason}</span>
                      <span className="text-gray-500">{formatTimestamp(event.createdAt)}</span>
                    </div>
                    <div className="text-gray-600 mt-1">
                      {event.actorEmail} ({event.actorRole})
                    </div>
                    <div className="text-gray-600">mode: {newMode}</div>
                    <div className="text-gray-600">
                      enabled: {String(event.oldPolicyEnabled)} → {String(event.newPolicyEnabled)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
